"use client";

import { Flame } from "lucide-react";
import { MentorMark } from "@/app/_module/components/MentorMark";
import { formatTime } from "./helpers";
import type { Message } from "./TeamChatService";

function getGrade(score: number): string {
  if (score >= 90) return "S";
  if (score >= 80) return "A";
  if (score >= 70) return "B";
  if (score >= 60) return "C";
  if (score >= 50) return "D";
  return "F";
}

// One scale for the score, same thresholds the training history uses.
function toneFor(score: number): string {
  if (score >= 80) return "text-emerald-600 dark:text-emerald-400";
  if (score >= 60) return "text-amber-600 dark:text-amber-400";
  return "text-rose-600 dark:text-rose-400";
}

const DIFFICULTY_LABEL: Record<string, string> = {
  easy: "Rookie",
  medium: "Veteran",
  hard: "Elite",
};

export interface InsuraTrainingScoreCardProps {
  message: Message;
}

export function InsuraTrainingScoreCard({ message }: InsuraTrainingScoreCardProps) {
  const meta = message.metadata as any;
  const overall = meta?.scoreOverall ?? 0;
  const scenario = meta?.scenarioName || "Training Session";
  const difficulty = meta?.difficulty || "medium";
  const bestDim = meta?.bestDimension || null;
  const bestScore = meta?.bestScore ?? null;
  const worstDim = meta?.worstDimension || null;
  const worstScore = meta?.worstScore ?? null;
  const duration = meta?.duration || null;
  const streak = meta?.streak || null;
  const agent = meta?.agentName || message.sender?.name || "Agent";
  const grade = getGrade(overall);
  const tone = toneFor(overall);

  return (
    <div className="flex gap-3 px-4 py-2">
      <div className="w-8 h-8 mt-0.5 flex-shrink-0 rounded-full border border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
        <MentorMark className="w-4 h-4 text-zinc-600 dark:text-zinc-300" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-semibold text-sm text-zinc-800 dark:text-zinc-100">InsuraMentor</span>
          <span className="text-[11px] text-zinc-400 dark:text-zinc-500">{formatTime(message.createdAt)}</span>
        </div>

        <div className="max-w-lg rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden">
          {/* Header: what it was */}
          <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-2">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                Training complete
              </p>
              <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 truncate">{scenario}</p>
            </div>
            <span className="flex-shrink-0 rounded-md border border-zinc-200 dark:border-zinc-700 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500 dark:text-zinc-400">
              {DIFFICULTY_LABEL[difficulty] || "Veteran"}
            </span>
          </div>

          {/* Score + who */}
          <div className="flex items-end gap-3 px-4 pb-3">
            <span className={`text-4xl font-bold leading-none tabular-nums ${tone}`}>{overall}</span>
            <span className={`text-lg font-semibold leading-none mb-0.5 ${tone} opacity-60`}>{grade}</span>
            <div className="flex-1 min-w-0 flex items-center justify-end gap-2">
              <span className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{agent}</span>
              {streak > 0 && (
                <span className="inline-flex items-center gap-1 rounded-md border border-zinc-200 dark:border-zinc-700 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500 dark:text-zinc-400">
                  <Flame className="w-3 h-3" />
                  {streak}d
                </span>
              )}
            </div>
          </div>

          {/* Breakdown */}
          {(bestDim || worstDim || duration) && (
            <div className="flex divide-x divide-zinc-200 dark:divide-zinc-700 border-t border-zinc-200 dark:border-zinc-700">
              {bestDim && (
                <div className="flex-1 px-4 py-2 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Strongest</p>
                  <p className="text-xs text-zinc-700 dark:text-zinc-200 truncate">
                    {bestDim} <span className="tabular-nums text-zinc-400 dark:text-zinc-500">{bestScore}</span>
                  </p>
                </div>
              )}
              {worstDim && worstDim !== bestDim && (
                <div className="flex-1 px-4 py-2 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Work on</p>
                  <p className="text-xs text-zinc-700 dark:text-zinc-200 truncate">
                    {worstDim} <span className="tabular-nums text-zinc-400 dark:text-zinc-500">{worstScore}</span>
                  </p>
                </div>
              )}
              {duration && (
                <div className="flex-1 px-4 py-2 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-400 dark:text-zinc-500">Talk time</p>
                  <p className="text-xs text-zinc-700 dark:text-zinc-200 tabular-nums">
                    {Math.floor(duration / 60)}m {Math.floor(duration % 60)}s
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {message.content && (
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2 leading-relaxed">{message.content}</p>
        )}
      </div>
    </div>
  );
}
